import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const BASE = "/api-manager";

const ApiKeys = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchEndpoints = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${BASE}/admin/endpoints`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(`Failed to load API keys (${res.status}) ${err.error || ""}`.trim());
        return;
      }

      const data = await res.json();
      setEndpoints(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchEndpoints error:", err);
      setError("Network error — could not fetch API keys");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  const updateEndpoint = async (id, patch) => {
    setBusyId(id);
    try {
      const res = await fetch(`${BASE}/admin/endpoints/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(patch),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Update failed: ${err.error || res.status}`);
        return;
      }

      await fetchEndpoints();
    } catch (err) {
      console.error("updateEndpoint error:", err);
      alert("Network error — could not update key");
    } finally {
      setBusyId(null);
    }
  };

  const regenerate = (ep) => {
    if (!window.confirm(`Regenerate the key for "${ep.name}"? The old key will stop working immediately.`)) return;
    const newKey = "cbkey_" + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
    updateEndpoint(ep.id, { apiKey: newKey, authRequired: true });
  };

  const toggleRequired = (ep) => {
    const authRequired = !ep.authRequired;
    const patch = { authRequired };
    if (authRequired && !ep.apiKey) {
      patch.apiKey = "cbkey_" + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
    }
    updateEndpoint(ep.id, patch);
  };

  const deleteEndpoint = async (ep) => {
    if (!window.confirm(`Delete endpoint "${ep.name}" and its key permanently? This also stops serving ${ep.path}.`)) return;
    setBusyId(ep.id);
    try {
      const res = await fetch(`${BASE}/admin/endpoints/${ep.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Delete failed: ${err.error || res.status}`);
        return;
      }

      await fetchEndpoints();
    } catch (err) {
      console.error("deleteEndpoint error:", err);
      alert("Network error — could not delete endpoint");
    } finally {
      setBusyId(null);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
      })
      .catch(() => alert("Could not copy to clipboard"));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">API Keys</h1>
          <p className="text-sm text-slate-500 mt-1">
            Keys that protect the data endpoints published in API Manager. Create a new endpoint
            there to get a new key; manage existing keys here.
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/api-manager/endpoints?create=1")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition whitespace-nowrap"
        >
          + New Endpoint
        </button>
      </div>

      {error && (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading...</div>
      ) : !error && endpoints.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-slate-500 text-sm">
          No endpoints yet. Create one in API Manager → Endpoints to get your first API key.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Path</th>
                <th className="text-left px-4 py-3">Key</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {endpoints.map((ep) => {
                const outputUrl = `${window.location.origin}${BASE}${ep.path}`;
                const isBusy = busyId === ep.id;
                return (
                  <tr key={ep.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{ep.name}</div>
                      <div className="text-xs text-slate-400">
                        {ep.enabled === false ? "disabled" : "enabled"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => copy(outputUrl, `url-${ep.id}`)}
                        className="font-mono text-xs text-green-700 hover:underline text-left"
                        title="Click to copy full output URL"
                      >
                        {ep.path}
                      </button>
                      {copiedId === `url-${ep.id}` && (
                        <div className="text-[10px] text-green-600 mt-0.5">Copied!</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ep.authRequired ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                            {revealed[ep.id]
                              ? ep.apiKey
                              : "•".repeat(Math.min(ep.apiKey?.length || 12, 16))}
                          </code>
                          <button
                            className="text-xs text-slate-500 hover:text-slate-800"
                            onClick={() => setRevealed((r) => ({ ...r, [ep.id]: !r[ep.id] }))}
                          >
                            {revealed[ep.id] ? "Hide" : "Show"}
                          </button>
                          <button
                            className="text-xs text-blue-600 hover:underline"
                            onClick={() => copy(ep.apiKey, `key-${ep.id}`)}
                          >
                            {copiedId === `key-${ep.id}` ? "Copied!" : "Copy"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Public — no key required</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          ep.authRequired
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {ep.authRequired ? "Key required" : "Public"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-3 flex-wrap">
                        <button
                          disabled={isBusy}
                          onClick={() => toggleRequired(ep)}
                          className="text-xs text-slate-600 hover:underline disabled:opacity-40"
                        >
                          {ep.authRequired ? "Make public" : "Require key"}
                        </button>
                        {ep.authRequired && (
                          <button
                            disabled={isBusy}
                            onClick={() => regenerate(ep)}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-40"
                          >
                            Regenerate
                          </button>
                        )}
                        <button
                          disabled={isBusy}
                          onClick={() => deleteEndpoint(ep)}
                          className="text-xs text-red-600 hover:underline disabled:opacity-40"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
